import { useState } from "react";
import { Link, Outlet, useLocation, useRouteLoaderData, useSearchParams, useSubmit } from "react-router-dom";

import ServerFooter from "../common/ServerFooter";
import UserInfo from "src/components/common/userInfo";
import { useAuthStore, User } from "../main/authStore";

import { Purchase } from "./types";

export const extraFields = ["page", "limit", "poDateFrom", "poDateTo"];

export default function PurchaseList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const user = useAuthStore(state => state.user) as User;
    const submit = useSubmit();
    const location = useLocation();
    const {
        purchases,
        start: defaultStartDate,
        end: defaultEndDate,
        total,
    }: { purchases: Purchase[]; start: string; end: string; total: number } = useRouteLoaderData("Purchases")!;
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const onFilter = [...searchParams.keys()].filter(param => !extraFields.includes(param)).length > 0;

    const handlePaid = (purchase: Purchase) => {
        if (!window.confirm(`Are you sure to set this purchase ${purchase.paid === "yes" ? "unpaid" : "paid"}?`)) return;
        const formData = new FormData();
        formData.append("id", String(purchase.id));
        formData.append("paid", purchase.paid === "yes" ? "no" : "yes");
        submit(formData, {
            method: "post",
            action: "Paid?" + searchParams.toString(),
        });
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3">
                <span className = "me-2 fw-bold">PO Date Range</span>
                <label htmlFor = "poDateFrom" className = "text-nowrap me-2 mb-0">Start</label>
                <input
                    type = "date"
                    id = "poDateFrom"
                    className = "form-control inline me-3"
                    value = {startDate}
                    max = {endDate}
                    onChange = {e => setStartDate(e.currentTarget.value)}
                />
                <label htmlFor = "poDateTo" className = "text-nowrap me-2 mb-0">End</label>
                <input
                    type = "date"
                    id = "poDateTo"
                    className = "form-control inline me-3"
                    value = {endDate}
                    min = {startDate}
                    onChange = {e => setEndDate(e.currentTarget.value)}
                />
                <button
                    type = "button"
                    className = "form-control inline me-5"
                    disabled = {defaultStartDate === startDate && defaultEndDate === endDate}
                    onClick = {() => {
                        const params = new URLSearchParams(searchParams);
                        params.set("poDateFrom", startDate);
                        params.set("poDateTo", endDate);
                        setSearchParams(params, { replace: false });
                    }}
                >
                    Load
                </button>
                <Link className = "btn btn-outline-primary pointer position-relative" to = {{ pathname: "Filter", search: location.search }} replace>
                    Filter
                    {onFilter ? <span className = "badge text-bg-primary position-absolute top-0 start-100 translate-middle" style = {{ minWidth: "2.2em" }}>on</span> : null}
                </Link>
                <Link className = "btn btn-outline-primary pointer ms-3" to = {{ pathname: "Export", search: location.search }}>
                    Export
                </Link>
                {user.purchases === "edit" ? (
                    <Link className = "btn btn-outline-primary pointer ms-3" to = "Add">
                        Add
                    </Link>
                ) : null}
            </nav>
            {!purchases.length ? (
                <div className = "cssLoading">Empty</div>
            ) : (
                <div className = "table-responsive">
                    <table className = "table">
                        <thead className = "noTableTopBorder">
                            <tr>
                                <th>PO Number</th>
                                <th>PO Date</th>
                                <th>Requester</th>
                                <th>Project</th>
                                <th>Category</th>
                                <th>Total</th>
                                <th>Approver</th>
                                <th>Status</th>
                                <th>Paid</th>
                                {user.purchases === "paid" ? <th /> : null}
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {purchases.map(purchase => (
                                <tr key = {purchase.id}>
                                    <td>{purchase.poNumber}</td>
                                    <td>{purchase.poDate}</td>
                                    <td>
                                        <UserInfo name = {purchase.requesterName} id = {purchase.requesterId} />
                                    </td>
                                    <td>{purchase.projectId ? <Link to = {`/Projects/${purchase.projectId}`}>{purchase.projectName}</Link> : null}</td>
                                    <td>{purchase.category}</td>
                                    <td>${purchase.total}</td>
                                    <td>
                                        <UserInfo name = {purchase.approverName} id = {purchase.approverId} />
                                    </td>
                                    <td>{purchase.status}</td>
                                    <td>{purchase.paid}</td>
                                    {user.purchases === "paid" ? (
                                        <td>
                                            <button
                                                type = "button"
                                                className = {purchase.paid === "yes" ? "btn btn-info" : "btn btn-primary"}
                                                onClick = {() => handlePaid(purchase)}
                                            >
                                                {purchase.paid === "yes" ? "Unmark" : "Mark"}
                                            </button>
                                        </td>
                                    ) : null}
                                    <td>
                                        <Link className = "btn btn-outline-primary" to = {String(purchase.id)}>Detail</Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {total ? <ServerFooter length = {total} /> : null}
            <Outlet />
        </div>
    );
}
